import {
  compareAndSwapLocalWithdrawal,
  getLocalWithdrawal,
  saveRecoverySnapshot,
} from "../storage/offlineDatabase";

import { syncSingleWithdrawal } from "./syncService";
import { getLastKnownAuthUser } from "./authService";
import { nextLocalWithdrawalNumber } from "./withdrawalIdentityService";
import { registerMaterialentnahmeBackgroundSync } from "./serviceWorkerService";
import { WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION } from "./withdrawalDocumentConstants";

import type {
  LocalWithdrawalData,
  LocalWithdrawalRecord,
} from "../types/offlineWithdrawal";


export type CreateWithdrawalData =
  Omit<
    LocalWithdrawalData,
    "client_uuid"
  >;



export type WithdrawalPersistenceTelemetryPhase = "server_number_lookup" | "indexeddb_write" | "recovery_snapshot";
export type WithdrawalPersistenceTelemetrySink = (phase: WithdrawalPersistenceTelemetryPhase, durationMs: number) => void;

interface WithdrawalPersistenceOptions {
  autoSync?: boolean;
  telemetry?: WithdrawalPersistenceTelemetrySink;
}

export interface CreateWithdrawalInput {
  withdrawalData:
    CreateWithdrawalData;

  pdfBlob: Blob;
  pdfPreviewBlob?: Blob;
  labelBlob: Blob | null;
  sourceBlob?: Blob | null;
  detectionSourceBlob?: Blob | null;
  analysisInputBlob?: Blob | null;
  rectificationInputBlob?: Blob | null;
  ocrWarpBlob?: Blob | null;
  visionFeedbackBlob?: Blob | null;
  liveTrainingBlob?: Blob | null;
  debugImageBlob?: Blob | null;
  debugDataBlob?: Blob | null;

  pdfFilename?: string;
  pdfPreviewFilename?: string;
  labelFilename?: string;
  sourceFilename?: string;
  detectionSourceFilename?: string;
  analysisInputFilename?: string;
  rectificationInputFilename?: string;
  ocrWarpFilename?: string;
  visionFeedbackFilename?: string;
  liveTrainingFilename?: string;
  debugImageFilename?: string;
  debugDataFilename?: string;
}


// ---------------------------------------------------------
// UUID erzeugen
// ---------------------------------------------------------

function generateUuid(): string {
  if (
    typeof crypto !== "undefined"
    && typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  if (
    typeof crypto !== "undefined"
    && typeof crypto.getRandomValues === "function"
  ) {
    const bytes =
      new Uint8Array(16);

    crypto.getRandomValues(
      bytes
    );

    // UUID Version 4
    bytes[6] =
      (bytes[6] & 0x0f)
      | 0x40;

    // RFC-4122 Variant
    bytes[8] =
      (bytes[8] & 0x3f)
      | 0x80;

    const hex =
      Array.from(
        bytes,
        (byte) =>
          byte
            .toString(16)
            .padStart(2, "0")
      );

    return [
      hex.slice(0, 4).join(""),
      hex.slice(4, 6).join(""),
      hex.slice(6, 8).join(""),
      hex.slice(8, 10).join(""),
      hex.slice(10, 16).join(""),
    ].join("-");
  }

  /*
   * Nur Fallback für Entwicklungsumgebungen,
   * die weder randomUUID noch getRandomValues
   * bereitstellen.
   */
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (character) => {
      const random =
        Math.floor(
          Math.random() * 16
        );

      const value =
        character === "x"
          ? random
          : (random & 0x3) | 0x8;

      return value.toString(16);
    }
  );
}


// ---------------------------------------------------------
// Dateinamen erzeugen
// ---------------------------------------------------------

function sanitizeFilenamePart(
  value: string
): string {
  return value
    .trim()
    .replace(
      /[^A-Za-z0-9_-]+/g,
      "_"
    )
    .replace(
      /_+/g,
      "_"
    )
    .replace(
      /^_+|_+$/g,
      ""
    );
}


function withWithdrawalNumberPrefix(filename: string, localNumber: number): string {
  const prefix = `#${localNumber}_`;
  return filename.startsWith(prefix) ? filename : `${prefix}${filename}`;
}


function createDefaultPdfFilename(
  data: CreateWithdrawalData
): string {
  const materialNumber =
    sanitizeFilenamePart(
      data.material_number
    );

  const date =
    sanitizeFilenamePart(
      data.withdrawal_date
    );

  return (
    `${materialNumber}_${date}.pdf`
  );
}


function getImageExtension(
  blob: Blob
): string {
  switch (blob.type) {
    case "image/jpeg":
      return "jpg";

    case "image/webp":
      return "webp";

    case "image/png":
    default:
      return "png";
  }
}


function createDefaultPreviewFilename(
  data: CreateWithdrawalData
): string {
  const materialNumber = sanitizeFilenamePart(data.material_number);
  const date = sanitizeFilenamePart(data.withdrawal_date);
  return `${materialNumber}_${date}_Vorschau.webp`;
}

function createDefaultLabelFilename(
  data: CreateWithdrawalData,
  labelBlob: Blob
): string {
  const materialNumber =
    sanitizeFilenamePart(
      data.material_number
    );

  const date =
    sanitizeFilenamePart(
      data.withdrawal_date
    );

  const extension =
    getImageExtension(
      labelBlob
    );

  return (
    `${materialNumber}_${date}_Etikett.${extension}`
  );
}


// ---------------------------------------------------------
// Lokalen Datensatz erzeugen
// ---------------------------------------------------------

function buildLocalWithdrawalRecord(
  input: CreateWithdrawalInput,
  ownerUserId: number,
  ownerUsername: string,
  localNumber: number,
  clientUuidOverride?: string,
): LocalWithdrawalRecord {
  const now =
    new Date()
      .toISOString();

  const clientUuid = clientUuidOverride ?? generateUuid();

  const completeData:
    LocalWithdrawalData =
    {
      ...input.withdrawalData,

      client_uuid:
        clientUuid,

      floor:
        input.withdrawalData
          .floor
          .toUpperCase(),
    };

  return {
    client_uuid: clientUuid,
    record_type: "completed",
    owner_user_id: ownerUserId,
    owner_username: ownerUsername,
    local_withdrawal_number: localNumber,

    data:
      completeData,

    pdf_blob:
      input.pdfBlob,

    pdf_filename: withWithdrawalNumberPrefix(
      input.pdfFilename ?? createDefaultPdfFilename(input.withdrawalData),
      localNumber,
    ),

    pdf_preview_blob:
      input.pdfPreviewBlob ?? null,

    pdf_preview_filename:
      input.pdfPreviewBlob
        ? (input.pdfPreviewFilename ?? createDefaultPreviewFilename(input.withdrawalData))
        : null,

    label_blob:
      input.labelBlob,

    label_filename: input.labelBlob
      ? withWithdrawalNumberPrefix(input.labelFilename ?? createDefaultLabelFilename(input.withdrawalData, input.labelBlob), localNumber)
      : null,

    source_blob: input.sourceBlob ?? null,
    source_filename: input.sourceBlob
      ? withWithdrawalNumberPrefix(input.sourceFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_Originalaufnahme.jpg`, localNumber)
      : null,

    detection_source_blob: input.detectionSourceBlob ?? null,
    detection_source_filename: input.detectionSourceBlob
      ? withWithdrawalNumberPrefix(input.detectionSourceFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_DetectionQuelle.jpg`, localNumber)
      : null,

    analysis_input_blob: input.analysisInputBlob ?? null,
    analysis_input_filename: input.analysisInputBlob
      ? withWithdrawalNumberPrefix(input.analysisInputFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_OpenCV_Analyse.png`, localNumber)
      : null,

    rectification_input_blob: input.rectificationInputBlob ?? null,
    rectification_input_filename: input.rectificationInputBlob
      ? withWithdrawalNumberPrefix(input.rectificationInputFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_RectificationInput.png`, localNumber)
      : null,

    ocr_warp_blob: input.ocrWarpBlob ?? null,
    ocr_warp_filename: input.ocrWarpBlob
      ? withWithdrawalNumberPrefix(input.ocrWarpFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_OCR_Warp.jpg`, localNumber)
      : null,

    vision_feedback_blob: input.visionFeedbackBlob ?? null,
    vision_feedback_filename: input.visionFeedbackBlob
      ? withWithdrawalNumberPrefix(input.visionFeedbackFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_VisionFeedback.json`, localNumber)
      : null,

    live_training_blob: input.liveTrainingBlob ?? null,
    live_training_filename: input.liveTrainingBlob
      ? withWithdrawalNumberPrefix(input.liveTrainingFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_LiveTraining.json`, localNumber)
      : null,

    debug_image_blob: input.debugImageBlob ?? null,
    debug_image_filename: input.debugImageBlob ? withWithdrawalNumberPrefix(input.debugImageFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_Debug.jpg`, localNumber) : null,
    debug_data_blob: input.debugDataBlob ?? null,
    debug_data_filename: input.debugDataBlob ? withWithdrawalNumberPrefix(input.debugDataFilename ?? `${sanitizeFilenamePart(input.withdrawalData.material_number)}_${sanitizeFilenamePart(input.withdrawalData.withdrawal_date)}_Diagnose.json`, localNumber) : null,

    sync_status:
      "pending",

    server_withdrawal_id:
      null,

    server_withdrawal_number:
      null,

    base_version:
      null,

    created_at:
      now,

    updated_at:
      now,

    content_revision:
      1,

    document_artifact_version:
      WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION,

    document_server_refresh_required:
      true,

    last_sync_attempt_at:
      null,

    synced_at:
      null,

    last_error:
      null,
  };
}


// ---------------------------------------------------------
// Neue Entnahme abschließen
// ---------------------------------------------------------

export async function createWithdrawal(
  input: CreateWithdrawalInput,
  options: WithdrawalPersistenceOptions = {},
): Promise<LocalWithdrawalRecord> {
  /*
   * WICHTIG:
   *
   * Erst lokal speichern.
   *
   * Der erfolgreiche lokale Abschluss einer
   * Entnahme darf niemals davon abhängen,
   * ob der Server gerade erreichbar ist.
   */

  const owner = getLastKnownAuthUser();
  if (!owner) throw new Error("Für die lokale Entnahme ist kein angemeldeter Benutzer bekannt.");
  const numberStarted = performance.now();
  const localNumber = await nextLocalWithdrawalNumber(owner.id);
  options.telemetry?.("server_number_lookup", performance.now() - numberStarted);
  const record = buildLocalWithdrawalRecord(input, owner.id, owner.username, localNumber);

  const indexedDbStarted = performance.now();
  const stored = await compareAndSwapLocalWithdrawal(record.client_uuid, 0, (current) => {
    if (current) throw new Error("Die lokale Entnahme-ID ist bereits vergeben.");
    return record;
  });
  if (!stored) throw new Error("Lokale Entnahme konnte nicht atomar gespeichert werden.");
  options.telemetry?.("indexeddb_write", performance.now() - indexedDbStarted);
  const recoveryStarted = performance.now();
  await saveRecoverySnapshot(stored);
  options.telemetry?.("recovery_snapshot", performance.now() - recoveryStarted);
  void registerMaterialentnahmeBackgroundSync();


  // -------------------------------------------------------
  // Wenn möglich direkt synchronisieren
  // -------------------------------------------------------

  if (options.autoSync !== false && typeof navigator !== "undefined" && navigator.onLine) {
    void syncSingleWithdrawal(stored).catch(() => {
      // Der lokale Abschluss ist bereits sicher gespeichert; der Sync läuft entkoppelt im Hintergrund.
    });
  }


  // -------------------------------------------------------
  // Aktuellsten Zustand zurückgeben
  // -------------------------------------------------------

  return stored;
}

export async function createWithdrawalWithIdentity(
  input: CreateWithdrawalInput,
  clientUuid: string,
  localNumber: number,
  options: WithdrawalPersistenceOptions = {},
): Promise<LocalWithdrawalRecord> {
  const owner = getLastKnownAuthUser();
  if (!owner) throw new Error("Für die lokale Entnahme ist kein angemeldeter Benutzer bekannt.");
  const existing = await getLocalWithdrawal(clientUuid);
  if (existing && existing.owner_user_id !== owner.id) throw new Error("Dieser lokale Vorgang gehört zu einem anderen Benutzer.");
  const expectedRevision = Math.max(0, existing?.content_revision ?? 0);
  const indexedDbStarted = performance.now();
  const stored = await compareAndSwapLocalWithdrawal(clientUuid, expectedRevision, (current) => {
    if (current && current.owner_user_id !== owner.id) {
      throw new Error("Dieser lokale Vorgang gehört zu einem anderen Benutzer.");
    }
    const record = buildLocalWithdrawalRecord(input, owner.id, owner.username, localNumber, clientUuid);
    record.content_revision = expectedRevision + 1;
    if (current) {
      record.server_withdrawal_id = current.server_withdrawal_id;
      record.server_withdrawal_number = current.server_withdrawal_number;
      record.local_withdrawal_number = current.server_withdrawal_number ?? current.local_withdrawal_number ?? localNumber;
      // Beim Finalisieren eines Drafts bleiben bereits lokal archivierte technische
      // Scan-/Lernartefakte erhalten, auch wenn der fortgesetzte Workflow sie nicht
      // erneut in React-State laden musste. Die finale Feedback-Datei darf sie ersetzen.
      record.source_blob ??= current.source_blob ?? null;
      record.source_filename ??= current.source_filename ?? null;
      record.detection_source_blob ??= current.detection_source_blob ?? null;
      record.detection_source_filename ??= current.detection_source_filename ?? null;
      record.analysis_input_blob ??= current.analysis_input_blob ?? null;
      record.analysis_input_filename ??= current.analysis_input_filename ?? null;
      record.rectification_input_blob ??= current.rectification_input_blob ?? null;
      record.rectification_input_filename ??= current.rectification_input_filename ?? null;
      record.ocr_warp_blob ??= current.ocr_warp_blob ?? null;
      record.ocr_warp_filename ??= current.ocr_warp_filename ?? null;
      record.live_training_blob ??= current.live_training_blob ?? null;
      record.live_training_filename ??= current.live_training_filename ?? null;
      record.debug_image_blob ??= current.debug_image_blob ?? null;
      record.debug_image_filename ??= current.debug_image_filename ?? null;
      record.debug_data_blob ??= current.debug_data_blob ?? null;
      record.debug_data_filename ??= current.debug_data_filename ?? null;
    }
    return record;
  });
  if (!stored) throw new Error("Lokaler Vorgang konnte nicht atomar abgeschlossen werden.");
  options.telemetry?.("indexeddb_write", performance.now() - indexedDbStarted);
  const recoveryStarted = performance.now();
  await saveRecoverySnapshot(stored);
  options.telemetry?.("recovery_snapshot", performance.now() - recoveryStarted);
  void registerMaterialentnahmeBackgroundSync();
  if (options.autoSync !== false && typeof navigator !== "undefined" && navigator.onLine) {
    void syncSingleWithdrawal(stored).catch(() => undefined);
  }
  return stored;
}


export async function queueWithdrawalEdit(
  input: CreateWithdrawalInput,
  identity: {
    clientUuid: string;
    withdrawalId: number;
    withdrawalNumber: number;
    baseVersion: number;
  },
  options: WithdrawalPersistenceOptions = {},
): Promise<LocalWithdrawalRecord> {
  const owner = getLastKnownAuthUser();
  if (!owner) throw new Error("Für die Bearbeitung ist kein angemeldeter Benutzer bekannt.");
  const existing = await getLocalWithdrawal(identity.clientUuid);
  if (existing && existing.owner_user_id !== owner.id) {
    throw new Error("Dieser Vorgang gehört lokal zu einem anderen Benutzer.");
  }
  const expectedRevision = Math.max(0, existing?.content_revision ?? 0);
  const indexedDbStarted = performance.now();
  const stored = await compareAndSwapLocalWithdrawal(identity.clientUuid, expectedRevision, (current) => {
    if (current && current.owner_user_id !== owner.id) {
      throw new Error("Dieser Vorgang gehört lokal zu einem anderen Benutzer.");
    }
    const record = buildLocalWithdrawalRecord(
      input,
      owner.id,
      owner.username,
      identity.withdrawalNumber,
      identity.clientUuid,
    );
    record.record_type = "edit";
    record.content_revision = expectedRevision + 1;
    record.server_withdrawal_id = identity.withdrawalId;
    record.server_withdrawal_number = identity.withdrawalNumber;
    record.base_version = identity.baseVersion;
    return record;
  });
  if (!stored) throw new Error("Lokale Bearbeitung konnte nicht atomar gespeichert werden.");
  options.telemetry?.("indexeddb_write", performance.now() - indexedDbStarted);
  const recoveryStarted = performance.now();
  await saveRecoverySnapshot(stored);
  options.telemetry?.("recovery_snapshot", performance.now() - recoveryStarted);
  void registerMaterialentnahmeBackgroundSync();

  if (options.autoSync !== false && typeof navigator !== "undefined" && navigator.onLine) {
    void syncSingleWithdrawal(stored).catch(() => undefined);
  }
  return stored;
}
